import type React from 'react';
import {useState} from 'react';
import {Autocomplete, IconButton, Stack, TextField, Typography} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {useDebouncedSearch} from '~/hooks/useDebouncedSearch';
import {useUserSearchQuery} from '~/pages/supplier-360/tabs/SupplierOverview/hooks/useUserSearchQuery';
import {type SupplierContactUser, supplierContactUserName} from '~/pages/supplier-360/types/Supplier360Types';

const USER_OPTION_COUNT = 20;

type Props = {
    /** The name saved on the plan; empty until somebody is picked. */
    value: string;
    readOnly: boolean;
    placeholder?: string;
    /** The picked user's display name, or `''` when the field is cleared. */
    onPick: (name: string) => void;
};

/**
 * A plan field that holds a person: shows the saved name, and on click offers only accounts from the shared `/users/`
 * endpoint (searched on the server, debounced). Nothing can be typed in as free text; a set name can be cleared.
 * Each opening starts from an empty search, so the list matches the empty input.
 */
export const UserPickField: React.FC<Props> = ({value, readOnly, placeholder = 'Not set', onPick}) => {
    const [editing, setEditing] = useState(false);
    const {debounced: search, setInput: setInputValue, clear: clearSearch} = useDebouncedSearch();
    const {data: users = [], isFetching} = useUserSearchQuery(search, USER_OPTION_COUNT, editing);

    const open = () => {
        clearSearch();
        setEditing(true);
    };

    if (!editing) {
        return (
            <Stack direction="row" alignItems="center" sx={{minWidth: 0, '&:hover svg': {visibility: 'visible'}}}>
                <Typography variant="body2" noWrap
                            role={readOnly ? undefined : 'button'} tabIndex={readOnly ? undefined : 0}
                            onClick={readOnly ? undefined : open}
                            onKeyDown={readOnly ? undefined : e => e.key === 'Enter' && open()}
                            sx={{cursor: readOnly ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 0.5,
                                px: 0.5, py: 0.25, minWidth: 0}}>
                    <span>{value || placeholder}</span>
                    {!readOnly && <EditOutlinedIcon sx={{fontSize: 14, color: 'text.secondary', visibility: 'hidden'}}/>}
                </Typography>
                {!readOnly && value && (
                    <IconButton size="small" aria-label="Clear" title="Clear" onClick={() => onPick('')} sx={{p: 0.25}}>
                        <CloseIcon sx={{fontSize: 14, color: 'text.secondary', visibility: 'hidden'}}/>
                    </IconButton>
                )}
            </Stack>
        );
    }

    return (
        <Autocomplete<SupplierContactUser>
            size="small" fullWidth autoFocus openOnFocus open
            options={users} loading={isFetching} value={null}
            filterOptions={options => options}
            onInputChange={(_, v, reason) => reason === 'input' && setInputValue(v)}
            onChange={(_, user) => {
                setEditing(false);
                if (user) onPick(supplierContactUserName(user));
            }}
            onClose={() => setEditing(false)}
            getOptionLabel={u => u.email ? `${supplierContactUserName(u)} (${u.email})` : supplierContactUserName(u)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={params => <TextField {...params} autoFocus placeholder={value || 'Search users'}/>}
        />
    );
};
